/* eslint-disable @blitz/block-scope-case */
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getBrowserInfo } from '~/utils/browser';
import { useLoaderData } from '@remix-run/react';
import { genTemplateGitHub } from '~/utils/templates';
import { chatStore } from '~/lib/stores/chat';
import { useStore } from '@nanostores/react';
import { workbenchStore } from '~/lib/stores/workbench';
import { getMessagesById } from '~/lib/persistence/db';
import { projectId, db } from '~/lib/persistence/useChatHistory';
import JSZip from 'jszip';
import fileSaver from 'file-saver';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';

type MenuLink = {
  type: 'link' | 'bug' | 'feature' | 'action';
  icon: React.ReactNode;
  title: string;
  url: string;
  delay?: number;
  action?: () => void;
};

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'flex';

interface HelpMenuProps {
  position?: Position;
  buttonClassName?: string;
  withBg?: boolean;
  framework?: string;
}

const HelpMenu: React.FC<HelpMenuProps> = ({
  position = 'bottom-right',
  buttonClassName,
  withBg = false,
  framework,
}) => {
  const { t } = useTranslation();
  const chat = useStore(chatStore);
  const { NEXT_PUBLIC_GITHUB_REPO } = useLoaderData<Env>();
  const [isOpen, setIsOpen] = useState(false);
  const [effectivePosition, setEffectivePosition] = useState<Exclude<Position, 'flex'>>(
    position === 'flex' ? 'top-left' : position,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (position === 'flex' && buttonRef.current && isOpen) {
      // Get button position and dimensions
      const rect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Calculate available space in each direction
      const spaceAbove = rect.top;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = windowWidth - rect.right;

      // Determine best position - only for menu opening direction
      const verticalPosition = spaceBelow > spaceAbove ? 'top' : 'bottom';
      const horizontalPosition = spaceRight > spaceLeft ? 'left' : 'right';

      setEffectivePosition(`${verticalPosition}-${horizontalPosition}` as Exclude<Position, 'flex'>);
    } else if (position !== 'flex') {
      setEffectivePosition(position);
    }
  }, [isOpen, position]);

  const createGithubIssueUrl = (item: MenuLink) => {
    const baseUrl = `https://github.com/${NEXT_PUBLIC_GITHUB_REPO}/issues/new`;
    const frameworkLabels = framework?.toLowerCase().split('_');
    const bugLabels = [item.type === 'bug' ? 'bug' : 'enhancement', ...(frameworkLabels || [])];
    const enhancedBody = genTemplateGitHub({ type: item.type as 'bug' | 'feature', framework });
    const params = new URLSearchParams();

    params.append('title', item.title);
    params.append('body', enhancedBody);
    params.append('labels', bugLabels.join(','));

    return `${baseUrl}?${params.toString()}`;
  };

  const { trackCallBooked } = usePosthogCommonEvents();

  const handleDownloadProjectBundle = async () => {
    try {
      const currentProjectId = projectId.get();

      if (!db || !currentProjectId) {
        throw new Error(t('helpMenu.projectDataUnavailable', 'Project data unavailable. Try again in a moment.'));
      }

      const [codeZipBlob, chat] = await Promise.all([
        workbenchStore.getZipBlob(),
        getMessagesById(db, currentProjectId),
      ]);

      const outerZip = new JSZip();
      outerZip.file('code.zip', codeZipBlob);

      const messagesArray = (chat && (chat as any).messages) || [];
      const messagesTxt = JSON.stringify(messagesArray, null, 2);
      outerZip.file('messages.txt', messagesTxt);

      const baseName = ((chat as any)?.description || 'project').toLowerCase().split(' ').join('_');
      const timestampHash = Date.now().toString(36).slice(-6);
      const outerBlob = await outerZip.generateAsync({ type: 'blob' });

      const { saveAs } = fileSaver as unknown as { saveAs: (data: Blob, filename: string) => void };
      saveAs(outerBlob, `${baseName}_${timestampHash}_dualite_bundle.zip`);

      toast.success(t('helpMenu.downloadSuccess', 'Project bundle downloaded successfully!'));
      setIsOpen(false); // Close the help menu
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during project bundle download',
          framework,
        },
      });
      console.error('Project bundle download failed:', error);
      toast.error(t('helpMenu.downloadError', 'Failed to download project bundle. Please try again.'));
    }
  };

  const handleLinkClick = (link: MenuLink) => {
    if (link.type === 'action' && link.action) {
      link.action();
      return;
    }

    if (link.type === 'bug' || link.type === 'feature') {
      window.open(createGithubIssueUrl(link), '_blank', 'noopener,noreferrer');
      return;
    }

    // Track Calendly / call bookings
    if (link.url.includes('calendly.com')) {
      try {
        trackCallBooked('help_menu');
      } catch (error) {
        console.error('[PostHog] Failed to track call_booked from HelpMenu:', error);
      }
    }

    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Position-based styles - fixed positioning only for preset positions
  const menuContainerStyle =
    position === 'flex'
      ? { position: 'relative' as const } // Just make it relative for proper menu positioning
      : {
          position: 'fixed' as const,
          top: effectivePosition.includes('top') ? '1.5rem' : 'auto',
          bottom: effectivePosition.includes('bottom') ? '1.5rem' : 'auto',
          left: effectivePosition.includes('left') ? '1.5rem' : 'auto',
          right: effectivePosition.includes('right') ? '1.5rem' : 'auto',
        };

  // Animation values based on position
  const getAnimationValues = () => {
    switch (effectivePosition) {
      case 'top-left':
        return {
          initial: { opacity: 0, y: -20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: -20 },
          menuClassName: 'absolute top-full left-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: -20 },
        };
      case 'top-right':
        return {
          initial: { opacity: 0, y: -20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: -20, x: 20 },
          menuClassName: 'absolute top-full right-0 flex flex-col gap-2 mt-3',
          itemAnimation: { x: 20 },
        };
      case 'bottom-left':
        return {
          initial: { opacity: 0, y: 20, x: -20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: -20 },
          menuClassName: 'absolute bottom-full left-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: -20 },
        };
      case 'bottom-right':
        return {
          initial: { opacity: 0, y: 20, x: 20 },
          animate: { opacity: 1, y: 0, x: 0 },
          exit: { opacity: 0, y: 20, x: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
      default:
        return {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 20 },
          menuClassName: 'absolute bottom-full right-0 flex flex-col gap-2 mb-3',
          itemAnimation: { x: 20 },
        };
    }
  };

  const animationValues = getAnimationValues();

  // Dynamic button className based on whether we show text
  const defaultButtonClassName = !chat.started
    ? 'flex items-center gap-2 justify-center my-2 px-4 py-2 rounded-full bg-dualite-background-button hover:bg-dualite-background-button-hover text-white shadow-lg border border-dualite-border-secondary transition-colors duration-200'
    : 'flex items-center justify-center my-2 h-8 w-8 rounded-full bg-dualite-background-button hover:bg-dualite-background-button-hover text-white shadow-lg border border-dualite-border-secondary transition-colors duration-200';

  const finalButtonClassName = buttonClassName || defaultButtonClassName;

  const links: MenuLink[] = [
    ...(chat.started
      ? [
          {
            type: 'action' as const,
            icon: <div className="i-ph:archive-light"></div>,
            title: t('helpMenu.downloadProjectBundle', 'Download Project Bundle'),
            url: '#',
            delay: 0.05,
            action: handleDownloadProjectBundle,
          },
        ]
      : []),
    {
      type: 'link' as const,
      icon: <div className="i-ph:video-light"></div>,
      title: t('helpMenu.videoGuides', 'Video Guides'),
      url: 'https://www.youtube.com/playlist?list=PLcoySjNMrqYTSoW0QlaOWDSWk7IcrieIr',
      delay: 0.1,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:book-open-light"></div>,
      title: t('helpMenu.documentation', 'Documentation'),
      url: 'https://alphadocs.dualite.dev',
      delay: 0.2,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:discord-logo-light"></div>,
      title: t('helpMenu.discordCommunity', 'Discord Community'),
      url: 'https://discord.gg/vCXsN3hYpf',
      delay: 0.3,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:envelope-light"></div>,
      title: t('helpMenu.emailUs', 'Email Us'),
      url: 'mailto:info@dualite.dev',
      delay: 0.4,
    },
    {
      type: 'link' as const,
      icon: <div className="i-ph:headset-light"></div>,
      title: t('helpMenu.speakToTeam', 'Speak to the Team'),
      url: 'https://calendly.com/singhvirohan/dualite-feedback',
      delay: 0.5,
    },
  ];

  return (
    <div className="z-50 inline-block" style={menuContainerStyle}>
      {/* Menu Items */}
      {isOpen && (
        <motion.div
          className={animationValues.menuClassName}
          initial={animationValues.initial}
          animate={animationValues.animate}
          exit={animationValues.exit}
          style={
            withBg
              ? {
                  backgroundColor: 'var(--dualite-background-secondary)',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--dualite-border-secondary)',
                  boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.1)',
                }
              : {}
          }
        >
          {links.map((link, index) => (
            <motion.div
              onClick={() => handleLinkClick(link)}
              key={index}
              className="cursor-pointer flex items-center gap-2 bg-dualite-background-button hover:bg-dualite-background-button-hover text-dualite-text-primary px-4 py-2 rounded-full text-sm transition-colors duration-150 border border-dualite-border-secondary shadow-md whitespace-nowrap"
              initial={{ opacity: 0, ...animationValues.itemAnimation }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: link.delay || index * 0.1 }}
            >
              {link.icon}
              {link.title}
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Help Button */}
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={finalButtonClassName}

        /*
         * whileHover={{ scale: 1.05 }}
         * whileTap={{ scale: 0.95 }}
         */
      >
        {isOpen ? (
          <div className="i-ph:x-bold text-xl text-dualite-text-primary" />
        ) : (
          <>
            <div className="i-ph:question text-2xl text-dualite-text-primary" />
            {!chat.started && (
              <span className="text-sm font-semibold ml-2 text-dualite-text-primary">{t('helpMenu.help', 'Help')}</span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

export default HelpMenu;
